import { useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MetricDependency } from "overlay-engine";
import { ReportWidget } from "./reportWidgetShared";
import {
  ReportWidgetTooltipControls,
  TooltipPopoverContent,
  TooltipMorePopover,
} from "../../editor/TooltipMenu";
import { LabeledDropdown } from "./LabeledDropdown";
import { MetricLoadingDots } from "../components/MetricLoadingDots";
import {
  useOverlaySources,
  useRelatedOverlay,
} from "../hooks/useOverlaySources";
import { GeostatsLayer, isGeostatsLayer } from "@seasketch/geostats-types";
import { CaretDownIcon, Pencil2Icon } from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import {
  combinePresenceTableMetrics,
  PresenceTableValue,
} from "./IntersectingFeaturesList.utils";
import { ReportUIStateContext } from "../context/ReportUIStateContext";
import {
  applyBufferSettingsToParameters,
  BufferSelector,
  getBufferSettingsFromDependencies,
} from "./BufferSelector";
type IntersectingFeaturesListSettings = {
  labelColumn?: string;
  maxDisplayItems?: number;
  hiddenColumns?: string[];
};

const DEFAULT_MAX_DISPLAY_ITEMS = 10;

export const IntersectingFeaturesList: ReportWidget<
  IntersectingFeaturesListSettings
> = ({ metrics, componentSettings, sources, loading }) => {
  const { t } = useTranslation("reports");
  const { printing } = useContext(ReportUIStateContext);
  const [showAll, setShowAll] = useState(false);

  const labelColumn = componentSettings.labelColumn;
  const maxDisplayItemsSetting =
    componentSettings.maxDisplayItems ?? DEFAULT_MAX_DISPLAY_ITEMS;
  // Use 0 to represent "all" - show all items
  const maxDisplayItems =
    maxDisplayItemsSetting === 0 ? Infinity : maxDisplayItemsSetting;

  const tableData = useMemo(() => {
    return combinePresenceTableMetrics(metrics, loading);
  }, [metrics, loading]);

  // Get available label columns from geostats
  const availableLabelColumns = useMemo(() => {
    const source = sources?.[0];
    if (!source?.geostats) return [];

    const geoLayer = isGeostatsLayer(
      (source.geostats as any)?.layers?.[0] as GeostatsLayer
    )
      ? ((source.geostats as any).layers[0] as GeostatsLayer)
      : undefined;

    if (!geoLayer?.attributes) return [];

    // Get all unique property keys from the values
    const keys = new Set<string>();
    for (const value of tableData.values) {
      if (value && typeof value === "object") {
        for (const key in value) {
          if (key !== "__id") {
            keys.add(key);
          }
        }
      }
    }

    // Filter to only attributes that exist in both geostats and the values
    return geoLayer.attributes
      .filter((attr) => keys.has(attr.attribute))
      .map((attr) => attr.attribute)
      .sort();
  }, [sources, tableData.values]);

  // Determine the label column to use
  const effectiveLabelColumn = useMemo(() => {
    if (labelColumn && availableLabelColumns.includes(labelColumn)) {
      return labelColumn;
    }
    // Fall back to first available column, or first property in values
    if (availableLabelColumns.length > 0) {
      return availableLabelColumns[0];
    }
    // Last resort: use first property from values
    if (tableData.values.length > 0) {
      const firstValue = tableData.values[0];
      if (firstValue && typeof firstValue === "object") {
        for (const key in firstValue) {
          if (key !== "__id") {
            return key;
          }
        }
      }
    }
    return undefined;
  }, [labelColumn, availableLabelColumns, tableData.values]);

  // Get all property keys from values (for displaying in accordion)
  // Filter out hidden columns
  const allPropertyKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const value of tableData.values) {
      if (value && typeof value === "object") {
        for (const key in value) {
          if (key !== "__id") {
            keys.add(key);
          }
        }
      }
    }
    const hiddenColumns = componentSettings.hiddenColumns || [];
    return Array.from(keys)
      .filter((key) => !hiddenColumns.includes(key))
      .sort();
  }, [tableData.values, componentSettings.hiddenColumns]);

  const displayedValues =
    showAll || printing
      ? tableData.values
      : tableData.values.slice(0, maxDisplayItems);
  const hasMore =
    maxDisplayItems !== Infinity && tableData.values.length > maxDisplayItems;

  if (!loading && tableData.values.length === 0) {
    if (tableData.combineError) {
      return (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div>
            <Trans ns="reports">Unable to display intersecting features.</Trans>
          </div>
          <div className="mt-1 text-xs text-amber-700">
            {tableData.combineError}
          </div>
        </div>
      );
    }
    return (
      <div className="mt-3 border border-black/10 rounded bg-gray-50 px-3 py-2 text-gray-600 text-sm">
        <Trans ns="reports">No overlapping features found.</Trans>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="space-y-1">
        {loading && displayedValues.length === 0 ? (
          <div className="px-3 py-2 bg-white border border-gray-200 rounded text-sm">
            <MetricLoadingDots />
          </div>
        ) : (
          displayedValues.map((value, index) => {
            const labelValue =
              effectiveLabelColumn && value[effectiveLabelColumn];
            const label =
              labelValue != null
                ? String(labelValue)
                : `${t("Feature")} ${index + 1}`;
            return (
              <FeatureAccordionItem
                key={value.__id ?? index}
                title={label}
                value={value}
                propertyKeys={allPropertyKeys}
                forceOpen={printing}
              />
            );
          })
        )}
        {hasMore && !showAll && !printing && (
          <div className="w-full text-center pt-2">
            <button
              onClick={() => setShowAll(true)}
              className="px-3 py-1 rounded-full border border-gray-300 text-sm hover:bg-gray-50 inline-flex items-center space-x-2"
            >
              <span>
                <Trans ns="reports">Show all</Trans>
              </span>
              <span className="bg-slate-100 rounded px-1 py-0.5 text-xs text-gray-700">
                {tableData.values.length}
              </span>
            </button>
          </div>
        )}
        {hasMore && showAll && !printing && (
          <div className="w-full text-center pt-2">
            <button
              onClick={() => setShowAll(false)}
              className="px-3 py-1 rounded-full border border-gray-300 text-sm hover:bg-gray-50"
            >
              {t("Show less")}
            </button>
          </div>
        )}
      </div>
      {tableData.exceededLimit && (
        <div className="mt-2 text-xs text-gray-500 text-center">
          <Trans ns="reports">
            Showing first {{ limit: tableData.maxResults }} results.
          </Trans>
        </div>
      )}
    </div>
  );
};

function FeatureAccordionItem({
  title,
  value,
  propertyKeys,
  forceOpen = false,
}: {
  title: string;
  value: PresenceTableValue;
  propertyKeys: string[];
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;

  return (
    <div className="border rounded text-sm border-gray-300 bg-slate-50 overflow-clip">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`px-2 py-1 text-left w-full flex items-center space-x-1 ${
          isOpen ? "border-b border-gray-300" : ""
        }`}
      >
        <div className="flex-1 truncate">{title}</div>
        <CaretDownIcon
          className={`w-4 h-4 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="max-h-64 overflow-y-auto bg-white">
          <table className="w-full">
            <tbody>
              {propertyKeys.map((prop, index) => {
                const propValue = value[prop];
                return (
                  <tr
                    className={`border-b last:border-none border-slate-200 text-left ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                    key={prop}
                  >
                    <td className="font-thin p-1 px-2 text-left w-1/3 truncate">
                      {prop}
                    </td>
                    <td className="p-1 px-2">
                      {propValue != null ? String(propValue) : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const IntersectingFeaturesListTooltipControls: ReportWidgetTooltipControls =
  ({ node, onUpdate, onUpdateDependencyParameters }) => {
    const { t } = useTranslation("admin:reports");
    const settings: IntersectingFeaturesListSettings = useMemo(
      () => node.attrs?.componentSettings || {},
      [node.attrs?.componentSettings]
    );

    const dependencies = useMemo(
      () => (node.attrs?.metrics || []) as MetricDependency[],
      [node.attrs?.metrics]
    );

    const { filteredSources: sources } = useOverlaySources(dependencies);

    // Get available label columns from geostats
    const labelColumnOptions = useMemo(() => {
      const source = sources?.[0];
      if (!source?.geostats) return [];

      const geoLayer = isGeostatsLayer(
        (source.geostats as any)?.layers?.[0] as GeostatsLayer
      )
        ? ((source.geostats as any).layers[0] as GeostatsLayer)
        : undefined;

      if (!geoLayer?.attributes) return [];

      return geoLayer.attributes
        .map((attr) => attr.attribute)
        .sort()
        .map((attr) => ({
          value: attr,
          label: attr,
        }));
    }, [sources]);

    // Get all available columns from geostats attributes
    // These represent all possible columns that could appear in the values
    const allAvailableColumns = useMemo(() => {
      const source = sources?.[0];
      if (!source?.geostats) {
        return [];
      }

      const geoLayer = isGeostatsLayer(
        (source.geostats as any)?.layers?.[0] as GeostatsLayer
      )
        ? ((source.geostats as any).layers[0] as GeostatsLayer)
        : undefined;

      if (!geoLayer?.attributes) {
        return [];
      }

      return geoLayer.attributes.map((attr) => attr.attribute).sort();
    }, [sources]);

    const handleUpdate = (patch: Partial<IntersectingFeaturesListSettings>) => {
      onUpdate({
        componentSettings: {
          ...settings,
          ...patch,
        },
      });
    };

    const handleColumnToggle = (columnName: string, checked: boolean) => {
      const hiddenColumns = settings.hiddenColumns || [];
      if (checked) {
        // Column should be visible - remove from hiddenColumns
        const updated = hiddenColumns.filter((col) => col !== columnName);
        handleUpdate({
          hiddenColumns: updated.length > 0 ? updated : undefined,
        });
      } else {
        // Column should be hidden - add to hiddenColumns
        handleUpdate({
          hiddenColumns: [...hiddenColumns, columnName],
        });
      }
    };

    const [isColumnsPopoverOpen, setIsColumnsPopoverOpen] = useState(false);

    const relatedOverlay = useRelatedOverlay(dependencies);

    const bufferSettings = useMemo(
      () => getBufferSettingsFromDependencies(dependencies),
      [dependencies]
    );
    const showBufferGeography = useMemo(
      () => dependencies.some((d) => d.subjectType === "geographies"),
      [dependencies]
    );

    return (
      <div className="flex gap-3 items-center text-sm text-gray-800">
        {labelColumnOptions.length > 0 && (
          <LabeledDropdown
            label={t("label column")}
            value={settings.labelColumn || labelColumnOptions[0]?.value || ""}
            options={labelColumnOptions}
            onChange={(val) => handleUpdate({ labelColumn: val })}
          />
        )}
        <LabeledDropdown
          label={t("max displayed")}
          value={
            settings.maxDisplayItems === 0
              ? "all"
              : String(settings.maxDisplayItems ?? DEFAULT_MAX_DISPLAY_ITEMS)
          }
          options={[
            { value: "5", label: "5" },
            { value: "8", label: "8" },
            { value: "10", label: "10" },
            { value: "12", label: "12" },
            { value: "20", label: "20" },
            { value: "25", label: "25" },
            { value: "all", label: t("all") },
          ]}
          onChange={(val) =>
            handleUpdate({
              maxDisplayItems: val === "all" ? 0 : Number(val),
            })
          }
        />
        {allAvailableColumns.length > 0 && (
          <Popover.Root
            open={isColumnsPopoverOpen}
            onOpenChange={setIsColumnsPopoverOpen}
          >
            <Popover.Trigger asChild>
              <button
                type="button"
                className="h-6 bg-transparent text-gray-900 text-sm px-1 border-none rounded inline-flex items-center gap-1.5 hover:bg-gray-100 active:bg-gray-100 focus:bg-gray-100 data-[state=open]:bg-gray-100 focus:outline-none"
              >
                <Pencil2Icon className="w-3 h-3" />
                {t("columns")}
              </button>
            </Popover.Trigger>
            <TooltipPopoverContent title={t("columns")}>
              <div className="space-y-2 px-1 max-h-64 overflow-y-auto">
                {allAvailableColumns.map((columnName) => {
                  const hiddenColumns = settings.hiddenColumns || [];
                  const isVisible = !hiddenColumns.includes(columnName);
                  return (
                    <label
                      key={columnName}
                      className="flex items-center text-gray-700 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-2"
                        checked={isVisible}
                        onChange={(e) =>
                          handleColumnToggle(columnName, e.target.checked)
                        }
                      />
                      <span className="flex-1">{columnName}</span>
                    </label>
                  );
                })}
              </div>
            </TooltipPopoverContent>
          </Popover.Root>
        )}
        <TooltipMorePopover>
          <BufferSelector
            distanceKm={bufferSettings.distanceKm}
            bufferGeography={bufferSettings.bufferGeography}
            showBufferGeography={showBufferGeography}
            onChange={(next) => {
              onUpdateDependencyParameters((dependency) =>
                applyBufferSettingsToParameters(dependency, next)
              );
            }}
          />
          <div className="flex">
            <span className="text-sm font-light text-gray-400 whitespace-nowrap pr-1">
              {t("Component Type")}
            </span>
            <span className="text-sm font-light whitespace-nowrap px-1 flex-1 text-right">
              {t("Intersecting Features List")}
            </span>
          </div>
          {relatedOverlay && (
            <div className="flex">
              <span className="text-sm font-light text-gray-400 whitespace-nowrap pr-1">
                {t("Layer")}
              </span>
              <span className="text-sm font-light whitespace-nowrap px-1 flex-1 text-right truncate">
                {relatedOverlay.tableOfContentsItem?.title || "Unknown"}
              </span>
            </div>
          )}
        </TooltipMorePopover>
      </div>
    );
  };
